import React, { MouseEvent, useRef, useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';

import arrowIcon from '@/assets/icons/dashboard-avator-dropdown-arrow.svg';
import dummyImage from '@/assets/images/avatarPlaceholder.png';
import { useDetectClickOutside } from '@/helpers/hooks';
import cn from '@/helpers/utils/classNames';
import { CustomLink } from '@/types/Common';
import { Box, Popper } from '@mui/material';

interface IngredientDropdownButtonProps<T extends Record<string, any>> {
  name: string;
  className?: string;
  avatorURL?: string;
  options: CustomLink[];
  heading: string;
  customerId: number;
}

const IngredientDropdownButton = <T extends Record<string, any>>({
  options,
  name,
  className,
  avatorURL,
  heading,
  customerId,
}: IngredientDropdownButtonProps<T>) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const containerRef = useRef<null | HTMLDivElement>(null);
  const open = Boolean(anchorEl);

  useDetectClickOutside(() => {
    if (anchorEl) {
      setAnchorEl(null);
    }
  }, containerRef);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(open ? null : event.currentTarget);
    if (containerRef.current) {
      return setMenuWidth(containerRef.current.offsetWidth);
    }
  };

  return (
    <div className={cn('v2-relative', className)} ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        id="basic-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        className={cn('v2-border-none v2-outline-none v2-bg-transparent v2-flex v2-gap-[6px]')}
      >
        <Image
          src={avatorURL || dummyImage || ''}
          width={36}
          height={36}
          alt=""
          className="v2-rounded-full v2-object-cover v2-border-[0.5px] v2-border-[#00000014]"
        />

        <div className="v2-flex v2-gap-1 v2-items-center v2-justify-between">
          <span className="v2-font-sweet-sans-pro v2-font-normal v2-text-sm v2-text-[#101828] v2-truncate">{name}</span>
          <Image src={arrowIcon} alt="" className={cn('v2-transition-all', open && 'v2-rotate-180')} />
        </div>
      </button>

      <Popper
        id="basic-button"
        open={open}
        anchorEl={anchorEl}
        placement="bottom"
        popperOptions={{
          placement: 'bottom',
        }}
        disablePortal={true}
        style={{ bottom: 0, position: 'absolute', zIndex: 3 }}
      >
        <Box
          sx={{
            border: '1px solid #EAECF0',
            boxShadow: '0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
            backgroundColor: '#fff',
            width: menuWidth,
            marginTop: '6px',
            padding: '8px 4px',
            borderRadius: '4px',
            maxHeight: '338px',
            overflow: 'hidden',
            overflowY: 'auto',
          }}
        >
          <ul>
            <li className="v2-px-3 md:v2-px-6 v2-pt-1">
              <p className="v2-text-sm v2-font-sweet-sans-pro-medium v2-font-medium v2-text-[#344054] v2-text-wrap v2-max-w-[308px]">
                {heading}
              </p>
            </li>
            <li className="v2-px-3 md:v2-px-6 pb-1">
              <p className="v2-text-sm v2-font-sweet-sans-pro-medium v2-font-medium v2-text-[#344054] v2-text-wrap v2-max-w-[308px]">
                ID# {customerId}
              </p>
            </li>
            {options?.map((option, i) => (
              <li className="v2-px-3 md:v2-px-6 v2-py-1 v2-rounded-xs hover:v2-bg-[#C5D1CC]" key={i}>
                {option.onClick ? (
                  <button
                    type="button"
                    className="v2-text-sm v2-font-sweet-sans-pro v2-font-normal v2-text-[#344054] v2-text-wrap v2-max-w-[308px] v2-bg-transparent v2-border-none v2-cursor-pointer v2-w-full v2-text-left"
                    onClick={e => {
                      e.preventDefault();
                      option.onClick?.(e as any);
                    }}
                  >
                    {option.name}
                  </button>
                ) : option.as === 'a' ? (
                  <a
                    href={option.link}
                    className="v2-text-sm v2-font-sweet-sans-pro v2-font-normal v2-text-[#344054] v2-text-wrap v2-max-w-[308px]"
                    key={i}
                    onClick={() => localStorage.setItem('sidebar-nav-clicked', 'true')}
                  >
                    {option.name}
                  </a>
                ) : (
                  <Link
                    href={option.link}
                    className="v2-text-sm v2-font-sweet-sans-pro v2-font-normal v2-text-[#344054] v2-text-wrap v2-max-w-[308px]"
                    key={i}
                    onClick={() => localStorage.setItem('sidebar-nav-clicked', 'true')}
                  >
                    {option.name}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </Box>
      </Popper>
    </div>
  );
};

export default IngredientDropdownButton;
